import { Analytics } from '@util/Analytics';
import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import * as images from '../../components/assets/Images';
import { Color } from '../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { copyWhite } from '../assets/images/Icons';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout, PresentationLayoutConfig } from './layouts/PresentationLayout';

export type ShareLinkScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ShareLink,
  PresentationLayoutConfig,
  ScreenNames
>;

type ShareLinkScreenProps<ScreenNames extends string> = NavigationProps<
  ShareLinkScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ShareLinkScreen = <ScreenNames extends string>(
  props: ShareLinkScreenProps<ScreenNames>
): ReactElement => {
  const {
    navigation,
    layout: { content, ...restLayout },
  } = props;
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const user = useAppStore(AppSelector.user);
  const shareLink = `https://www.tryfinito.com/quit-buddy/?referrer=${user.name ?? 'Your friend'}`;

  function copyLinkToClipboard(link: string) {
    navigator.clipboard.writeText(link);
    setCopiedToClipboard(true);
    Analytics.track({
      event: Analytics.Event.o_app_referral_action,
      action: 'linkCopied',
    });
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        onPressPrimaryButton={() => navigation.next(props)}
        content={[
          ...content,
          {
            type: 'custom',
            content: (
              <CopyLinkContainer>
                <ReferralInputContainer>
                  <LinkInput value={shareLink} disabled={true} />
                  <ReferralCopyButton onClick={() => copyLinkToClipboard(shareLink)}>
                    <img width="16px" height="16px" src={copyWhite} alt="Copy icon" />
                  </ReferralCopyButton>
                </ReferralInputContainer>
                <Tooltip isVisible={copiedToClipboard}>Copied to clipboard</Tooltip>
              </CopyLinkContainer>
            ),
          },
          {
            type: 'image',
            src: images.emailReferralIllustration,
            altText: 'Email referral illustration',
          },
        ]}
        {...restLayout}
      />
    </ScreenWrapper>
  );
};

const CopyLinkContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LinkInput = styled.input`
  border-radius: 3px;
  border: solid 1px ${Color.decorative.accent};
  width: 100%;
  padding: ${({ theme }) => theme.spacing._12px};
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;

  :focus {
    outline: none;
  }
`;

const ReferralInputContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ReferralCopyButton = styled.button`
  border-radius: 3px;
  border: solid 1px ${Color.decorative.accent};
  background: ${Color.decorative.accent};
  padding: ${({ theme }) => theme.spacing._12px};
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  cursor: pointer;
`;

const Tooltip = styled.div<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  position: absolute;
  top: 54px;
  left: calc(50% -90px);
  right: calc(50% -90px);
  width: 180px;
  background-color: black;
  color: #fff;
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing._6px} 0`};
  border-radius: 6px;

  // tooltip arrow
  ::after {
    content: ' ';
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent black transparent;
  }
`;
